import { Box, Flex, Link } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import OTPInput from '@/components/Molecules/OTPInput';
import { useResendOTP, useVerifyOTP } from '@/hooks/service-hooks/auth';
import { processLoginData } from '@/utils/processLoginData';

const TwoFactorOTPForm = ({
	email,
	onSuccess,
}: {
	email: string;
	onSuccess: (isFirstLogin: boolean) => void;
}) => {
	const [otp, setOtp] = useState('');
	const [isVerifying, setIsVerifying] = useState(false);
	const [resendTimer, setResendTimer] = useState(30);
	const [canResend, setCanResend] = useState(false);

	const { mutate: verifyOTP } = useVerifyOTP(
		async (data) => {
			setIsVerifying(false); 
			const success = await processLoginData(data);
			if (success) {
				toast.success('OTP verified successfully');
				const isFirstLogin = data?.user?.userInformation?.isFirstLogin || false;
				onSuccess(isFirstLogin);
			}
		},
		(error) => {
			setIsVerifying(false);
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Invalid OTP. Please try again.',
			);
		},
	);

	const { mutate: resendOTP, isLoading: isResending } = useResendOTP(
		() => {
			toast.success('OTP resent successfully');
			setResendTimer(30);
			setCanResend(false);
		},
		(error) => {
			const apiError = error as {
				response?: { data?: { message?: string } };
			};
			toast.error(
				apiError?.response?.data?.message ||
					'Failed to resend OTP. Please try again.',
			);
		},
	);

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => {
				setResendTimer((prev) => prev - 1);
			}, 1000);
			return () => clearTimeout(timer);
		} else {
			setCanResend(true);
		}
	}, [resendTimer]);

	const handleVerify = () => {
		if (otp.length !== 6) {
			toast.error('Please enter the complete 6-digit OTP');
			return;
		}
		setIsVerifying(true);
		verifyOTP({ otp, email });
	};

	const handleResend = () => {
		if (canResend && !isResending) {
			resendOTP({ email });
		}
	};

	return (
		<Box>
			<Flex flexDirection="column" gap={4}>
				<Flex flexDirection="column" gap={1}>
					<FaceliftText
						fontSize="xl"
						fontWeight="bold"
						color="brand.textDark"
						textAlign="start"
					>
						Two-Factor Authentication
					</FaceliftText>
					<FaceliftText
						fontSize="sm"
						color="brand.textDark"
						textAlign="start"
					>
						For your security, please enter the 6-digit verification code sent
						to your registered device.
					</FaceliftText>
				</Flex>

				<Flex flexDirection="column" gap={1}>
					<FaceliftText
						fontSize="sm"
						color="brand.textDark"
						textAlign="start"
						fontWeight="medium"
					>
						Code sent to {email ? email : "your email address"}
					</FaceliftText>
				</Flex>

				<Box>
					<OTPInput
						length={6}
						onChange={setOtp}
						value={otp}
						disabled={isVerifying}
					/>
				</Box>

				<FaceliftButton
					mt={1}
					onClick={handleVerify}
					borderRadius="6px"
					size="md"
					transition="all 0.3s ease"
					_hover={{
						boxShadow: `0 0 10px 3px`,
						border: 'none',
						transform: 'scale(1.02)',
					}}
					letterSpacing={'widest'}
					loading={isVerifying}
					disabled={isVerifying || otp.length !== 6}
				>
					Verify and Continue
				</FaceliftButton>

				<Flex flexDirection="column" gap={1} alignItems="center">
					<FaceliftText fontSize="sm" color="brand.textDark">
						Didn't receive a code?
					</FaceliftText>
					{canResend ? (
						<Link
							onClick={isResending ? () => {} : handleResend}
							color="brand.solid"
							cursor={isResending ? 'not-allowed' : 'pointer'}
							textDecoration="underline"
							fontSize="sm"
							opacity={isResending ? 0.6 : 1}
						>
							{isResending ? 'Resending...' : 'Resend code'}
						</Link>
					) : (
						<FaceliftText fontSize="sm" color="brand.solid">
							Resend code ({String(Math.floor(resendTimer / 60)).padStart(2, '0')}:
							{String(resendTimer % 60).padStart(2, '0')})
						</FaceliftText>
					)}
				</Flex>
			</Flex>
		</Box>
	);
};

export default TwoFactorOTPForm;
